/**
 * Platform TOS Slice
 *
 * Manages platform Terms of Service acceptance state.
 * Tracks current TOS version, user's accepted version, and re-acceptance requirements.
 *
 * @see PRP-4Q25-009 for TOS requirements
 */
import {
  PLATFORM_TOS_CONTENT,
  PLATFORM_TOS_EFFECTIVE_DATE,
  PLATFORM_TOS_REQUIRES_REACCEPTANCE,
  PLATFORM_TOS_VERSION,
} from '@/content/legal';

import type { StateCreator } from 'zustand';

const TOS_STORAGE_KEY = 'gw-platform-tos-accepted';

export interface PlatformTOSSlice {
  // State
  currentTOSVersion: string | null;
  acceptedTOSVersion: string | null;
  requiresReacceptance: boolean;
  tosCheckLoading: boolean;
  tosContent: string | null;
  changesSummary: string | null;
  effectiveDate: string | null;
  hasCheckedTOS: boolean; // GW-529: Guard to prevent re-fetching

  // Actions
  checkPlatformTOS: () => void;
  acceptPlatformTOS: () => void;
  loadAcceptedVersion: () => void;
  clearTOSState: () => void;
}

const initialState = {
  currentTOSVersion: null,
  acceptedTOSVersion: null,
  requiresReacceptance: false,
  tosCheckLoading: false,
  tosContent: null,
  changesSummary: null,
  effectiveDate: null,
  hasCheckedTOS: false, // GW-529: Guard to prevent re-fetching
};

export const createPlatformTOSSlice: StateCreator<PlatformTOSSlice, [], [], PlatformTOSSlice> = (
  set,
  get
) => ({
  ...initialState,

  /**
   * Check current platform TOS version and compare with user's accepted version
   * GW-529: Added guards to prevent infinite re-fetching loops
   */
  checkPlatformTOS: () => {
    const state = get();
    if (state.tosCheckLoading) return;
    if (state.hasCheckedTOS && state.currentTOSVersion !== null) return;

    set({ tosCheckLoading: true });

    const acceptedVersion = localStorage.getItem(TOS_STORAGE_KEY);
    const needsReacceptance =
      PLATFORM_TOS_REQUIRES_REACCEPTANCE &&
      (!acceptedVersion || acceptedVersion !== PLATFORM_TOS_VERSION);

    set({
      currentTOSVersion: PLATFORM_TOS_VERSION,
      acceptedTOSVersion: acceptedVersion,
      requiresReacceptance: needsReacceptance,
      tosContent: PLATFORM_TOS_CONTENT,
      changesSummary: null,
      effectiveDate: PLATFORM_TOS_EFFECTIVE_DATE,
      tosCheckLoading: false,
      hasCheckedTOS: true,
    });
  },

  /**
   * Accept the current platform TOS version
   */
  acceptPlatformTOS: () => {
    const { currentTOSVersion } = get();
    if (!currentTOSVersion) return;

    localStorage.setItem(TOS_STORAGE_KEY, currentTOSVersion);

    set({
      acceptedTOSVersion: currentTOSVersion,
      requiresReacceptance: false,
    });
  },

  /**
   * Load previously accepted version from localStorage
   */
  loadAcceptedVersion: () => {
    const acceptedVersion = localStorage.getItem(TOS_STORAGE_KEY);
    if (acceptedVersion) {
      set({ acceptedTOSVersion: acceptedVersion });
    }
  },

  /**
   * Clear TOS state (on logout)
   */
  clearTOSState: () => {
    set(initialState);
  },
});
